/** Passkey/WebAuthn — gemeinsame Client-Hilfen (Login + Einstellungen). */

import { getPortalPbToken } from '../../constants';

export const WEBAUTHN_URL = '/api/webauthn';

/**
 * Passkey-Verwaltung (Registrieren, Auflisten, Löschen) wirkt auf ein konkretes Konto.
 * Der Dienst akzeptiert die customerId nur zusammen mit dem Session-Token dieses Kontos.
 * Nicht für den Login-Flow — dort besteht noch keine Sitzung.
 */
export function portalAuthHeaders(): Record<string, string> {
  const token = getPortalPbToken();
  if (!token) throw new Error('Keine aktive Portal-Sitzung — bitte neu anmelden.');
  return { Authorization: `Bearer ${token}` };
}

function b64urlToUint8(str: string): Uint8Array {
  const b64 = str.replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(b64), c => c.charCodeAt(0));
}

function uint8ToB64url(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function readApiJson(res: Response, fallback: string): Promise<Record<string, unknown>> {
  const text = await res.text();
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    if (text.trimStart().startsWith('<')) {
      throw new Error(
        'Passkey-Dienst nicht erreichbar (Server antwortet mit HTML statt JSON). Bitte später erneut versuchen.',
      );
    }
    throw new Error(fallback);
  }
}

export async function registerPasskey(
  customerId: string,
  email: string,
  kanzlei: string,
  deviceName: string,
) {
  // Früh prüfen, damit eine abgelaufene Sitzung nicht erst nach der Biometrie-Abfrage auffällt.
  const authHeaders = portalAuthHeaders();

  const cr = await fetch(`${WEBAUTHN_URL}/register/challenge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({ customerId, email, kanzlei }),
  });
  const challengeBody = await readApiJson(cr, 'Challenge-Fehler');
  if (!cr.ok) throw new Error(String(challengeBody.error || 'Challenge-Fehler'));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const options = challengeBody as any;

  options.challenge = b64urlToUint8(options.challenge);
  options.user.id = b64urlToUint8(options.user.id);
  if (options.excludeCredentials) {
    options.excludeCredentials = options.excludeCredentials.map(
      (c: { id: string; type: string }) => ({ ...c, id: b64urlToUint8(c.id) }),
    );
  }

  const cred = await navigator.credentials.create({ publicKey: options }) as PublicKeyCredential;
  const resp = cred.response as AuthenticatorAttestationResponse;

  const credJSON = {
    id: cred.id,
    rawId: uint8ToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: uint8ToB64url(resp.clientDataJSON),
      attestationObject: uint8ToB64url(resp.attestationObject),
    },
  };

  const vr = await fetch(`${WEBAUTHN_URL}/register/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({ customerId, credential: credJSON, deviceName }),
  });
  const verifyBody = await readApiJson(vr, 'Registrierung fehlgeschlagen');
  if (!vr.ok) throw new Error(String(verifyBody.error || 'Registrierung fehlgeschlagen'));
  return verifyBody;
}

export async function passkeyAuthenticate(email: string) {
  const challengeRes = await fetch(`${WEBAUTHN_URL}/auth/challenge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim().toLowerCase() }),
  });
  const challengeBody = await readApiJson(challengeRes, 'Challenge-Fehler');
  if (!challengeRes.ok) throw new Error(String(challengeBody.error || 'Challenge-Fehler'));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const options = challengeBody as any;

  options.challenge = b64urlToUint8(options.challenge);
  if (options.allowCredentials) {
    options.allowCredentials = options.allowCredentials.map((c: { id: string; type: string }) => ({
      ...c,
      id: b64urlToUint8(c.id),
    }));
  }

  const credential = await navigator.credentials.get({ publicKey: options }) as PublicKeyCredential;
  const resp = credential.response as AuthenticatorAssertionResponse;

  const credentialJSON = {
    id: credential.id,
    rawId: uint8ToB64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: uint8ToB64url(resp.clientDataJSON),
      authenticatorData: uint8ToB64url(resp.authenticatorData),
      signature: uint8ToB64url(resp.signature),
      userHandle: resp.userHandle ? uint8ToB64url(resp.userHandle) : null,
    },
  };

  const verifyRes = await fetch(`${WEBAUTHN_URL}/auth/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim().toLowerCase(), credential: credentialJSON }),
  });
  const verifyBody = await readApiJson(verifyRes, 'Verifizierung fehlgeschlagen');
  if (!verifyRes.ok) throw new Error(String(verifyBody.error || 'Verifizierung fehlgeschlagen'));
  return verifyBody;
}
